import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { AlarmClock, ChevronDown, ChevronRight, Mailbox, Workflow } from "lucide-mui";
import {
  api,
  prospectFilterKey,
  type ProspectFilters,
  type ProspectHitKind,
  type ProspectSort,
} from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import PageHeader from "../../components/PageHeader.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import LiveUpdatesPill from "../../components/LiveUpdatesPill.tsx";
import SavedViewsMenu from "../../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters } from "../../components/filters/useFilters.ts";
import ProspectTable, { ProspectRow, prospectColSpan } from "../../components/prospects/ProspectTable.tsx";
import { useCollectionFilter } from "../../components/prospects/useCollectionFilter.ts";
import { RUN_PARAM, useAgentRunFilter } from "../../components/prospects/useAgentRunFilter.ts";
import { useProspectListView } from "../../components/prospects/useProspectListView.ts";
import ReviewBulkBar from "../../components/review/ReviewBulkBar.tsx";
import TableCard from "../../components/table/TableCard.tsx";
import UrlTablePagination from "../../components/table/UrlTablePagination.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { NO_COUNTRY, useRememberedCountryFilter } from "../../hooks/useRememberedCountryFilter.ts";
import { useUiPreference } from "../../hooks/useUiPreference.ts";
import { useUrlPaging, useUrlSort, useUrlState } from "../../hooks/useUrlState.ts";
import { formatDate, formatNumber } from "../../utils/format.ts";
import ProspectDrawer from "./ProspectDrawer.tsx";
import { PROSPECT_REVIEW_COPY, useProspectReview } from "./prospectReview.ts";
import { type Queue, SHOW_OPTIONS, leavesView, queueFilters, showOption } from "./queues.ts";

// The countries a reviewer last filtered on, shared by the three pages and
// kept until they change it. A link's `?country=` wins.
const COUNTRIES_PREFERENCE = "prospects.countries";
const SNOOZED_OPEN_PREFERENCE = "prospects.worklist_snoozed_open";
const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";
const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "signal", label: "Signals" },
  { value: "agent", label: "Prospecting agents" },
  { value: "manual", label: "Added by hand" },
];
const MIN_HITS_OPTIONS = [
  { value: "2", label: "Seen more than once" },
  { value: "3", label: "3 or more hits" },
  { value: "5", label: "5 or more hits" },
];
const SORTS = new Set<string>(["last_hit", "first_hit", "hits", "company", "done"]);

const PAGE_TITLES: Record<Queue, string> = { inbox: "Inbox", worklist: "Worklist", done: "Done" };

function pageDescription(queue: Queue, tenantName: string): string {
  switch (queue) {
    case "inbox":
      return `Companies ${tenantName}'s collections, signals and agents found and nobody has decided on: continue, pursue or disqualify`;
    case "worklist":
      return `The companies ${tenantName} is pursuing. Snooze what can wait; mark them done once worked`;
    case "done":
      return `Companies ${tenantName} finished working; a new hit reopens them in the inbox`;
  }
}

/**
 * The tenant's prospects, one row per company whatever found it, in the queue
 * the route names: the inbox (nothing decided yet), the worklist (pursued), or
 * done. The same table, filters, bulk bar and drawer on each.
 */
export default function ProspectsPage({ queue }: { queue: Queue }) {
  const { selectedTenantId, selectedTenant, tenantPath } = useTenant();
  const { currentOrg } = useOrganization();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDecide = userHasGlobalScope(user, "prospecting:write");
  // Continuing a parked step runs the workflow: the scope that edits them.
  const canContinue = userHasGlobalScope(user, "signal_rules:write");
  const canViewWorkflows = userHasGlobalScope(user, "signal_rules:read");
  const url = useUrlState();
  const { searchParams, updateParams } = url;
  const paging = useUrlPaging(url, 50);
  const { sortBy, sortDir, handleSort } = useUrlSort(url, queue === "done" ? "done" : "last_hit", "desc");
  const sort = (SORTS.has(sortBy) ? sortBy : "last_hit") as ProspectSort;

  const showParam = searchParams.get("show");
  const show = queue === "inbox" ? showOption(showParam) : null;
  const snoozedOnly = queue === "worklist" && searchParams.get("snoozed") === "only";
  const kindParam = searchParams.get("kind");
  const kinds = kindParam ? (kindParam.split(",").filter(Boolean) as ProspectHitKind[]) : [];
  const agentFilter = searchParams.get("agent") ?? "";
  const workflowFilter = searchParams.get("workflow") ?? "";
  const listFilter = searchParams.get("collection_id") ?? "";
  const orderFilter = searchParams.get("order") ?? "";
  const runFilter = searchParams.get(RUN_PARAM) ?? "";
  const minHits = parseInt(searchParams.get("min_hits") ?? "", 10) || undefined;
  const newHits = searchParams.get("new_hits") === "yes";
  const sinceFilter = searchParams.get("since") ?? "";
  const q = searchParams.get("q") ?? "";
  const signalParam = searchParams.get("signal");

  const {
    countryFilter,
    countryOptions,
    loading: countriesLoading,
    filterState,
  } = useRememberedCountryFilter({
    url,
    preferenceKey: COUNTRIES_PREFERENCE,
    queryKey: keys.prospect.countries(selectedTenantId),
    loadCountries: () => (selectedTenantId ? api.getProspectCountries(selectedTenantId) : Promise.resolve([])),
  });

  const filters: ProspectFilters = {
    tenant_id: selectedTenantId ?? "",
    kinds: kinds.length ? kinds : undefined,
    agent_definition_id: agentFilter || undefined,
    workflow_id: workflowFilter || undefined,
    collection_id: listFilter || undefined,
    prospect_order_id: orderFilter || undefined,
    agent_run_id: runFilter || undefined,
    min_hits: minHits,
    new_hits: newHits || undefined,
    country: countryFilter.length ? countryFilter : undefined,
    since: sinceFilter || undefined,
    q: q || undefined,
    sort,
    dir: sortDir,
    ...queueFilters(queue, showParam, snoozedOnly),
  };

  // A link's `show` settles on the pill's own value.
  useEffect(() => {
    if (queue !== "inbox" || !showParam) return;
    const canonical = show === SHOW_OPTIONS[0] ? null : show!.value;
    if (showParam !== canonical) updateParams({ show: canonical }, true);
  }, [queue, showParam, show, updateParams]);

  // A link that names a signal (the dashboard's agent runs): open the prospect it
  // is a hit on, or fall back to the signal itself when it reached no company.
  useEffect(() => {
    if (!signalParam) return;
    let cancelled = false;
    api
      .getSignalResearch(signalParam)
      .then((research) => {
        if (cancelled) return;
        if (research.prospect_id) updateParams({ signal: null, prospect: research.prospect_id }, true);
        else navigate(tenantPath(`/workflows/signals?signal=${signalParam}`), { replace: true });
      })
      .catch(() => {
        if (!cancelled) updateParams({ signal: null }, true);
      });
    return () => {
      cancelled = true;
    };
  }, [signalParam, navigate, tenantPath, updateParams]);

  const { data: agents = [] } = useQuery({
    queryKey: keys.agentDefinition.list(selectedTenantId),
    queryFn: () => api.getAgentDefinitions(selectedTenantId),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60_000,
  });
  const { data: workflows = [] } = useQuery({
    queryKey: keys.workflow.list(selectedTenantId, null, null),
    queryFn: () => api.getWorkflows({ tenant_id: selectedTenantId ?? undefined }),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60_000,
  });
  const collectionSpec = useCollectionFilter(selectedTenantId, listFilter, "Found by");
  const runSpec = useAgentRunFilter(selectedTenantId, runFilter);

  const specs = useMemo<FilterSpec[]>(
    () => [
      ...(queue === "inbox"
        ? [
            {
              kind: "options",
              key: "show",
              label: "Show",
              param: "show",
              multiple: false,
              pinned: true,
              default: [SHOW_OPTIONS[0].value],
              noneValue: "all",
              noneLabel: "All prospects",
              options: SHOW_OPTIONS.filter((o) => o.value !== "all").map((o) => ({ value: o.value, label: o.label })),
            } satisfies FilterSpec,
          ]
        : []),
      {
        kind: "options",
        key: "kind",
        label: "Found by",
        param: "kind",
        pinned: true,
        options: KIND_OPTIONS,
      },
      {
        kind: "options",
        key: "min_hits",
        label: "Hits",
        param: "min_hits",
        multiple: false,
        pinned: true,
        noneLabel: "Any",
        options: MIN_HITS_OPTIONS,
      },
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading: countriesLoading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "agent",
        label: "Agent",
        param: "agent",
        group: "Found by",
        multiple: false,
        noneLabel: "Any agent",
        options: agents.map((a) => ({ value: a.id, label: a.name })),
        searchPlaceholder: "Search agents",
      },
      {
        kind: "options",
        key: "workflow",
        label: "Workflow",
        param: "workflow",
        group: "Found by",
        multiple: false,
        noneLabel: "Any workflow",
        options: workflows.map((w) => ({ value: w.id, label: w.name })),
        searchPlaceholder: "Search workflows",
      },
      runSpec,
      collectionSpec,
      {
        kind: "boolean",
        key: "new_hits",
        label: "New hits since decision",
        param: "new_hits",
        group: "Hits",
        yesOnly: true,
      },
      // Set by links: an order, an overview tile.
      {
        kind: "custom",
        key: "since",
        label: "Since",
        params: ["since"],
        summary: (get) => (get("since") ? formatDate(get("since")) : null),
      },
      {
        kind: "custom",
        key: "order",
        label: "Order",
        params: ["order"],
        summary: (get) => (get("order") ? "One prospect order" : null),
      },
      ...(queue === "worklist"
        ? [
            {
              kind: "custom",
              key: "snoozed",
              label: "Show",
              params: ["snoozed"],
              summary: (get) => (get("snoozed") === "only" ? "Snoozed only" : null),
            } satisfies FilterSpec,
          ]
        : []),
    ],
    [queue, countriesLoading, countryOptions, agents, workflows, runSpec, collectionSpec],
  );

  const filterBar = useFilters(specs, {
    state: filterState,
    search: { param: "q", placeholder: "Search companies" },
    pageKey: `prospects.${queue}`,
    scopeId: selectedTenant?.id,
    remember: true,
    // The countries are the reviewer's own preference, shared by the pages;
    // since / list / order / run / snoozed come from links (or are a one-off look)
    // and mean nothing on the next visit.
    // The inbox always opens on what needs a decision: a "Show" picked once (or
    // set by an overview link, "All prospects") would quietly list disqualified
    // and decided companies there on every later visit.
    notRemembered: ["country", "since", "collection_id", "order", RUN_PARAM, "snoozed", "show"],
  });

  const enabled = !!selectedTenantId;
  // The worklist's greyed-out section: what is still snoozed under the same
  // filters, its first page (`?snoozed=only` holds the rest).
  const snoozedFilters: ProspectFilters = { ...filters, snoozed: true };
  const snoozedKey = prospectFilterKey(snoozedFilters);
  const showSnoozedSection = queue === "worklist" && !snoozedOnly;
  const { data: snoozedRows } = useQuery({
    queryKey: keys.prospect.list(snoozedKey, 0),
    queryFn: () => api.getProspects({ ...snoozedFilters, limit: paging.rowsPerPage, offset: 0 }),
    enabled: enabled && showSnoozedSection,
    meta: { live: true },
  });
  const { data: snoozedCount } = useQuery({
    queryKey: keys.prospect.count(snoozedKey),
    queryFn: () => api.countProspects(snoozedFilters),
    enabled: enabled && showSnoozedSection,
    meta: { live: true },
  });
  const [snoozedOpen, setSnoozedOpen] = useUiPreference<boolean>(SNOOZED_OPEN_PREFERENCE, true, isBoolean);
  const view = useProspectListView({
    filters,
    url,
    paging,
    leaves: (decision) => leavesView(queue, showParam, snoozedOnly, decision),
    extraRows: (lastPage) => (showSnoozedSection && lastPage && snoozedOpen ? (snoozedRows ?? []) : []),
  });
  const { rows, count, total, prospectId } = view;
  // Its rows show under the last page's.
  const snoozedProspects = showSnoozedSection && view.lastPage ? (snoozedRows ?? []) : [];
  const showSnoozedOnly = () => updateParams({ snoozed: "only", prospect: null, page: null });

  const tableProps = {
    timeColumn: queue === "done" ? ("done" as const) : ("last_hit" as const),
    openId: prospectId,
    highlightedId: view.highlightedId,
    onOpen: view.open,
    scrollToRow: view.scrollToRow,
    selection: view.selection,
    canDecide,
    canContinue,
  };
  const colSpan = prospectColSpan(tableProps);
  const tenantName = selectedTenant?.name ?? "the tenant";

  const emptyCopy = snoozedOnly
    ? "Nothing snoozed"
    : queue === "worklist"
      ? "Nothing on the worklist. Pursue a prospect in the inbox to put it here"
      : queue === "done"
        ? filterBar.activeCount
          ? "No prospects match your filters"
          : "Nothing marked done yet"
        : show === SHOW_OPTIONS[0] && !filterBar.activeCount
          ? "Inbox zero: nothing waits for a decision"
          : "No prospects match your filters";

  return (
    <>
      <PageHeader
        title={PAGE_TITLES[queue]}
        description={pageDescription(queue, tenantName)}
        actions={
          queue === "inbox" ? (
            canViewWorkflows && (
              <Button component={RouterLink} to={tenantPath("/workflows")} variant="outlined" startIcon={<Workflow />}>
                Workflows
              </Button>
            )
          ) : (
            <Button component={RouterLink} to={tenantPath("/prospects/inbox")} variant="outlined" startIcon={<Mailbox />}>
              Inbox
            </Button>
          )
        }
      />

      <TableCard
        loading={view.isLoading}
        notice={<LiveUpdatesPill {...view.live} />}
        toolbar={
          <FilterBar
            filters={filterBar}
            count={count ? `${formatNumber(count.total)} ${count.total === 1 ? "prospect" : "prospects"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filterBar}
                context={{
                  tenantId: selectedTenant?.id,
                  tenantName: selectedTenant?.name,
                  organizationId: currentOrg?.id,
                  organizationName: currentOrg?.name,
                }}
              />
            }
          />
        }
        footer={<UrlTablePagination total={total} paging={paging} />}
      >
        <ProspectTable
          {...tableProps}
          rows={rows}
          removedIds={view.removedIds}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          emptyCopy={emptyCopy}
          after={
            snoozedProspects.length > 0 && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={colSpan} sx={{ bgcolor: "action.hover", py: 0.5 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() => setSnoozedOpen(!snoozedOpen)}
                        aria-label={snoozedOpen ? "Hide snoozed" : "Show snoozed"}
                        aria-expanded={snoozedOpen}
                      >
                        {snoozedOpen ? <ChevronDown fontSize="small" /> : <ChevronRight fontSize="small" />}
                      </IconButton>
                      <AlarmClock fontSize="small" sx={{ color: "text.secondary" }} />
                      <Typography variant="subtitle2">Snoozed</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", flexGrow: 1 }}>
                        {formatNumber(snoozedCount?.total ?? snoozedProspects.length)} · back on the worklist on their date
                      </Typography>
                      <Button size="small" onClick={showSnoozedOnly}>
                        Show only snoozed
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
                {snoozedOpen && snoozedProspects.map((p) => <ProspectRow key={p.id} prospect={p} muted {...tableProps} />)}
                {snoozedOpen && (snoozedCount?.total ?? 0) > snoozedProspects.length && (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <Button size="small" onClick={showSnoozedOnly}>
                        See all {formatNumber(snoozedCount!.total)} snoozed
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )
          }
        />
      </TableCard>

      {canDecide && (
        <ReviewBulkBar
          selected={view.selectedProspects}
          onClear={view.selection.clear}
          useReview={useProspectReview}
          copy={PROSPECT_REVIEW_COPY}
          canContinue={canContinue}
        />
      )}

      <ProspectDrawer
        prospectId={prospectId}
        onClose={view.close}
        contactId={view.contactId}
        onOpenContact={view.openContact}
        onCloseContact={view.closeContact}
        canDecide={canDecide}
        canContinue={canContinue}
        onReviewed={view.onReviewed}
        onStep={view.onStep}
      />
    </>
  );
}

export { NO_COUNTRY };
